<script lang="ts" setup>
import VueDatepicker, { DatePickerMarker } from '@vuepic/vue-datepicker';
import { DateTime } from 'luxon';
import { getLocale } from '@/utils/i18n/vueI18n';

const isOpened = ref(false);

interface Props {
  modelValue: Date | string | null;
  attachToParent?: boolean;
  parentToAttach?: HTMLElement;
  autoApply?: boolean;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  hideInput?: boolean;
  markers?: DatePickerMarker[];
  time?: boolean;
  compactView?: boolean;
  hideWeekNumbers?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  attachToParent: false,
  parentToAttach: undefined,
  autoApply: true,
  minDate: undefined,
  maxDate: undefined,
  disabled: false,
  hideInput: false,
  markers: undefined,
  time: false,
  compactView: false,
  hideWeekNumbers: false,
});

/**
 * Classes handed to the library for the teleported menu element.
 *
 * `dp--settling` is present until `onOpen` has finished repositioning: the menu
 * is mounted and painted before that correction runs, so for one frame it sits
 * at an unconstrained position — visible as a jump, and in a modal as the whole
 * dialog twitching when the menu briefly extends the document. Keying it off
 * `isOpened` rather than setting a flag inside `onOpen` means the very first
 * render is already covered, whenever the library happens to emit `@open`.
 *
 * `visibility` (not `display` or `opacity`) because it keeps the layout box, so
 * the library can still measure the menu to position it.
 */
const menuUi = computed(() => {
  const menu: string[] = [];
  if (props.compactView) {
    menu.push('dp--compact');
  }
  if (!isOpened.value) {
    menu.push('dp--settling');
  }

  return menu.length ? { menu } : {};
});

function close() {
  datepickerRef.value?.closeMenu();
}

defineExpose({ open, close, isOpened });

const emit = defineEmits<{
  (e: 'update:modelValue', date: Date | string | null): void;
  (e: 'onClose'): void;
}>();

const datepickerRef = ref();

const update = (dateOrString: Date | string | null) => {
  // Handle null/cleared values
  if (!dateOrString) {
    emit('update:modelValue', null);

    return;
  }

  const modelValueIsString =
    typeof props.modelValue === 'string' || props.modelValue === null;

  if (modelValueIsString) {
    if (typeof dateOrString === 'string') {
      // Check if the string is empty or invalid
      const trimmed = dateOrString.trim();
      if (!trimmed) {
        emit('update:modelValue', null);

        return;
      }
      emit('update:modelValue', DateTime.fromISO(trimmed).toISODate());

      return;
    }

    emit('update:modelValue', DateTime.fromJSDate(dateOrString).toISODate());

    return;
  }

  if (typeof dateOrString === 'string') {
    const trimmed = dateOrString.trim();
    if (!trimmed) {
      emit('update:modelValue', null);

      return;
    }
    emit('update:modelValue', DateTime.fromISO(trimmed).toJSDate());

    return;
  }

  emit('update:modelValue', dateOrString);
};

function open() {
  datepickerRef.value?.openMenu();
}

async function onOpen() {
  await nextTick(() => {
    // The library skips its viewport-overflow correction on first open (it
    // measures an offscreen copy, never the real position) and only corrects
    // on the next scroll/resize — visible as a jump. One reposition now makes
    // an overflowing menu align to the anchor immediately; it is a no-op when
    // the menu already fits.
    datepickerRef.value?.onScroll();
  });

  isOpened.value = true;
}

function onClose() {
  isOpened.value = false;
  emit('onClose');
}
</script>

<template>
  <VueDatepicker
    ref="datepickerRef"
    :model-value="props.modelValue"
    :auto-apply="props.autoApply"
    :enable-time-picker="props.time"
    :format="props.time ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'"
    :locale="getLocale()"
    :max-date="props.maxDate"
    :min-date="props.minDate"
    :start-date="props.minDate"
    :disabled="props.disabled"
    :markers="props.markers"
    :teleport="true"
    :text-input="true"
    :transitions="false"
    :month-name-format="'long'"
    :week-numbers="props.hideWeekNumbers ? undefined : 'iso'"
    :utc="props.time ? undefined : 'preserve'"
    :clearable="true"
    :ui="menuUi"
    :class="{
      'active-picker': isOpened,
      'hide-input': props.hideInput,
    }"
    @open="onOpen"
    @closed="onClose"
    @update:model-value="update"
    @cleared="update(null)"
  />
</template>

<style lang="scss">
.dp__main.hide-input {
  .dp__input_wrap {
    display: none !important;
  }
  &.active-picker {
    border: none;
  }
}

// Hidden for the frame between mount and `onOpen`'s reposition — see `menuUi`.
.dp__menu.dp--settling {
  visibility: hidden;
}

.dp__menu.dp--compact {
  // zoom (not transform: scale) so the compact size affects layout and is
  // already applied when the library measures the menu to position it.
  zoom: 0.75;

  .dp__menu_inner {
    padding: var(--space-4);
  }
  .dp__calendar_row {
    margin: 0;
  }
}

.dp__input {
  height: var(--control-height-lg);
  // The library's `.dp__input { outline: none }` loads after App.vue's ring.
  @include focus-ring;
}
.dp__input_icon_pad {
  margin-right: var(--space-12);
}
.dp__disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.active-picker {
  border: 1px solid var(--color-primary-300);
  background: var(--color-primary-50);
  border-radius: var(--border-radius-4);
}

.dp__menu {
  box-shadow: -4px 4px 20px -12px rgba(0, 0, 0, 0.64);
}

// The library's theme on the design tokens (WI 5799), on both elements that
// carry its theme class: the input's root and the teleported menu. The second
// class outranks the library's own `.dp__theme_light`, which loads after this
// file on the dev server and before it in production. Its z-index stays the
// library's 99999: the frozen table filter panel (z-index 9999) holds two
// pickers, and their menus must open above it.
.dp__theme_light:is(.dp__main, .dp__menu) {
  --dp-font-family: var(--content-font-family);
  --dp-border-radius: var(--border-radius-6);
  --dp-cell-border-radius: var(--border-radius-circle);
  --dp-background-color: var(--color-gray-0);
  --dp-text-color: var(--color-gray-900);
  --dp-hover-color: var(--color-primary-50);
  --dp-hover-text-color: var(--color-gray-900);
  --dp-hover-icon-color: var(--color-gray-500);
  --dp-primary-color: var(--color-primary-600);
  --dp-primary-text-color: var(--color-gray-0);
  // The days of the neighbouring months and the week numbers: text, 4.97:1.
  --dp-secondary-color: var(--color-gray-500);
  --dp-border-color: var(--color-gray-300);
  --dp-menu-border-color: var(--color-gray-200);
  --dp-border-color-hover: var(--color-gray-400);
  --dp-border-color-focus: var(--color-gray-400);
  --dp-disabled-color: var(--color-gray-100);
  --dp-disabled-color-text: var(--color-gray-400);
  --dp-icon-color: var(--color-gray-500);
  --dp-tooltip-color: var(--color-gray-50);
}

// A floating panel takes the kit's float radius (AppDropdown, UiSelect); the
// library gives the menu --dp-border-radius, the 6px of the controls.
.dp__menu.dp__theme_light {
  border-radius: var(--border-radius-8);
}

// Disabled days keep their light look (the library gives them the secondary
// colour).
.dp__theme_light .dp__cell_disabled {
  color: var(--color-gray-300);
}

.dp__action_buttons {
  display: none;
}
.dp__selection_preview {
  display: none;
}
.dp__arrow_top,
.dp__arrow_bottom {
  display: none;
}
.dp__month_year_select,
.dp__calendar_header_item {
  text-transform: uppercase;
}
.slide-fade-enter-active {
  // transition: all 0.3s ease-out;
  transition: all 3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
